import { useMemo } from 'react';

import { STORAGE_KEYS } from '@constants/storage';
import { getLocalStorageItem } from '@utils/storage';
import { LeadSourceResponse } from 'types/lead-management';

import {
  LeadSource,
  leadSourceColorMap,
  leadSourceOptions
} from '../constants';
import { useGetLeadSourcesQuery } from '../queries/LeadManagementQuery';

const DEFAULT_SOURCE_COLOR = leadSourceColorMap[LeadSource.FACEBOOK_ADS];

export interface SourceSelectItem {
  id: string;
  name: string;
}

export interface SourceOption {
  id: string;
  label: string;
  value: string;
}

export interface UseLeadSourcesResult {
  sources: LeadSourceResponse[];
  isLoading: boolean;
  sourceOptions: SourceOption[];
  sourceSelectItems: SourceSelectItem[];
  systemSourceNames: string[];
  getSourceLabel: (value?: string | null) => string;
  getSourceId: (value?: string | null) => string;
  getSourceColor: (value?: string | null) => string;
}

const useLeadSources = (): UseLeadSourcesResult => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { data, isLoading } = useGetLeadSourcesQuery({ organizationId });

  const sources = useMemo<LeadSourceResponse[]>(
    () => (data as any)?.data ?? [],
    [data]
  );

  const { byId, byName } = useMemo(() => {
    const idMap = new Map<string, LeadSourceResponse>();
    const nameMap = new Map<string, LeadSourceResponse>();
    sources.forEach((source) => {
      idMap.set(source.id, source);
      nameMap.set(source.name.toLowerCase(), source);
    });
    return { byId: idMap, byName: nameMap };
  }, [sources]);

  const resolveSource = useMemo(
    () =>
      (value?: string | null): LeadSourceResponse | undefined => {
        if (!value) return undefined;
        return byId.get(value) || byName.get(value.toLowerCase());
      },
    [byId, byName]
  );

  const getSourceLabel = useMemo(
    () =>
      (value?: string | null): string => {
        const name = resolveSource(value)?.name ?? value ?? '';
        return (
          leadSourceOptions[name as keyof typeof leadSourceOptions] || name
        );
      },
    [resolveSource]
  );

  const getSourceId = useMemo(
    () =>
      (value?: string | null): string =>
        resolveSource(value)?.id ?? '',
    [resolveSource]
  );

  const getSourceColor = useMemo(
    () =>
      (value?: string | null): string => {
        const name = resolveSource(value)?.name ?? value ?? '';
        return leadSourceColorMap[name] || DEFAULT_SOURCE_COLOR;
      },
    [resolveSource]
  );

  const sourceOptions: SourceOption[] = useMemo(
    () =>
      sources.map((source) => ({
        id: source.id,
        label: getSourceLabel(source.name),
        value: source.name
      })),
    [sources, getSourceLabel]
  );

  const sourceSelectItems: SourceSelectItem[] = useMemo(
    () =>
      sources.map((source) => ({
        id: source.id,
        name: getSourceLabel(source.name)
      })),
    [sources, getSourceLabel]
  );

  const systemSourceNames = useMemo(
    () =>
      sources
        .filter((source) => source.is_system)
        .map((source) => getSourceLabel(source.name)),
    [sources, getSourceLabel]
  );

  return {
    sources,
    isLoading,
    sourceOptions,
    sourceSelectItems,
    systemSourceNames,
    getSourceLabel,
    getSourceId,
    getSourceColor
  };
};

export default useLeadSources;
